/**
 * Player Card Drawer
 *
 * Floating indicator + expandable drawer for players to view their cards.
 * Shows cards dealt to the current player's characters.
 * Players can reveal cards to the whole party.
 *
 * Melnibonean aesthetic: gold accents, Georgia serif, dark bg.
 */

import { useState, useEffect } from 'react';
import { useCardStore } from '../stores/cardStore';
import { useCharacterStore } from '../stores/characterStore';
import { useCampaignStore } from '../stores/campaignStore';
import { Icon } from './Icon';
import type { PlayerCard, CardType } from '@machina/core';

// =============================================================================
// CONSTANTS
// =============================================================================

const SILVER = '#c0c0c0';
const SILVER_DIM = 'rgba(192, 192, 192, 0.4)';
const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.4)';

const CARD_TYPE_LABELS: Record<CardType, string> = {
  secret: 'Secret',
  item: 'Item',
  clue: 'Clue',
  rumor: 'Rumor',
  quest: 'Quest',
};

const CARD_TYPE_COLORS: Record<CardType, string> = {
  secret: '#a78bfa',
  item: '#60a5fa',
  clue: '#34d399',
  rumor: '#fbbf24',
  quest: '#f87171',
};

// =============================================================================
// CARD VIEW
// =============================================================================

function PlayerCardView({ card, onReveal }: { card: PlayerCard; onReveal: () => void }) {
  const [isRevealing, setIsRevealing] = useState(false);
  const typeColor = CARD_TYPE_COLORS[card.cardType];
  const isRevealed = card.visibility === 'revealed';

  const handleReveal = async () => {
    setIsRevealing(true);
    await onReveal();
    setIsRevealing(false);
  };

  return (
    <div
      style={{
        padding: 12,
        backgroundColor: 'rgba(10, 8, 12, 0.95)',
        borderRadius: 4,
        border: `1px solid ${isRevealed ? 'rgba(34, 197, 94, 0.4)' : GOLD_DIM}`,
        boxShadow: isRevealed ? '0 0 8px rgba(34, 197, 94, 0.1)' : `0 0 8px ${GOLD}10`,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span
          style={{
            fontSize: 9,
            padding: '2px 6px',
            borderRadius: 2,
            backgroundColor: `${typeColor}20`,
            color: typeColor,
            border: `1px solid ${typeColor}40`,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontWeight: 600,
          }}
        >
          {CARD_TYPE_LABELS[card.cardType]}
        </span>
        <span
          style={{
            flex: 1,
            fontSize: 14,
            fontWeight: 600,
            color: GOLD,
            fontFamily: 'Georgia, serif',
          }}
        >
          {card.title}
        </span>
        {isRevealed && (
          <span
            style={{
              fontSize: 9,
              padding: '2px 6px',
              borderRadius: 2,
              backgroundColor: 'rgba(34, 197, 94, 0.15)',
              color: '#22c55e',
              border: '1px solid rgba(34, 197, 94, 0.3)',
            }}
          >
            Revealed
          </span>
        )}
      </div>

      {/* Card text */}
      <div
        style={{
          fontSize: 12,
          color: '#f0e8f8',
          lineHeight: 1.6,
          fontFamily: 'Georgia, serif',
          padding: '8px 10px',
          backgroundColor: 'rgba(212, 175, 55, 0.05)',
          borderRadius: 3,
          borderLeft: `3px solid ${GOLD_DIM}`,
          marginBottom: 8,
        }}
      >
        {card.frontText}
      </div>

      {/* Reveal button */}
      {!isRevealed && (
        <button
          onClick={handleReveal}
          disabled={isRevealing}
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: 11,
            fontWeight: 600,
            fontFamily: 'Georgia, serif',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: 3,
            color: '#22c55e',
            cursor: isRevealing ? 'not-allowed' : 'pointer',
            opacity: isRevealing ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <Icon name="eye" size={12} color="#22c55e" />
          {isRevealing ? 'Revealing...' : 'Reveal to Party'}
        </button>
      )}
    </div>
  );
}

// =============================================================================
// PLAYER CARD DRAWER
// =============================================================================

export function PlayerCardDrawer() {
  const cards = useCardStore((s) => s.cards);
  const revealCard = useCardStore((s) => s.revealCard);
  const loadCards = useCardStore((s) => s.loadCards);
  const subscribeToRealtime = useCardStore((s) => s.subscribeToRealtime);
  const unsubscribeFromRealtime = useCardStore((s) => s.unsubscribeFromRealtime);
  const currentCampaignId = useCampaignStore((s) => s.currentCampaignId);
  const partyIds = useCharacterStore((s) => s.partyIds);
  const characters = useCharacterStore((s) => s.characters);
  const [isOpen, setIsOpen] = useState(false);

  // Load cards and subscribe to realtime when campaign changes
  useEffect(() => {
    if (!currentCampaignId) return;
    loadCards(currentCampaignId);
    subscribeToRealtime(currentCampaignId);
    return () => unsubscribeFromRealtime();
  }, [currentCampaignId, loadCards, subscribeToRealtime, unsubscribeFromRealtime]);

  // Get all character IDs that belong to the current player
  const myCharacterIds = new Set(partyIds.filter((id) => {
    const char = characters.get(id);
    return char && !char.isNpc;
  }));

  // Filter cards: private cards dealt to my characters + revealed cards
  const myCards = cards.filter(
    (c) =>
      (c.recipientId && myCharacterIds.has(c.recipientId) && c.visibility === 'private') ||
      c.visibility === 'revealed'
  );

  const privateCount = myCards.filter((c) => c.visibility === 'private').length;

  // Don't show anything if no cards
  if (myCards.length === 0) return null;

  return (
    <>
      {/* Floating indicator button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: 56,
          right: 332,
          zIndex: 950,
          padding: '8px 14px',
          backgroundColor: 'rgba(10, 8, 12, 0.95)',
          border: `1px solid ${privateCount > 0 ? GOLD : SILVER_DIM}`,
          borderRadius: 4,
          color: privateCount > 0 ? GOLD : SILVER,
          fontSize: 11,
          fontWeight: 600,
          fontFamily: 'Georgia, serif',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          boxShadow: privateCount > 0 ? `0 0 12px ${GOLD}30` : '0 2px 8px rgba(0,0,0,0.3)',
          transition: 'all 0.2s',
        }}
      >
        <Icon name="scroll" size={14} color={privateCount > 0 ? GOLD : SILVER} />
        Cards ({myCards.length})
        {privateCount > 0 && (
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: GOLD,
              boxShadow: `0 0 6px ${GOLD}`,
            }}
          />
        )}
      </button>

      {/* Drawer panel */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: 96,
            right: 332,
            width: 340,
            maxHeight: '50vh',
            zIndex: 950,
            backgroundColor: 'rgba(10, 8, 12, 0.98)',
            border: `1px solid ${GOLD_DIM}`,
            borderRadius: 6,
            boxShadow: `0 4px 24px rgba(0,0,0,0.5), 0 0 20px ${GOLD}10`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '10px 14px',
              borderBottom: `1px solid ${GOLD_DIM}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="scroll" size={14} color={GOLD} />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: GOLD,
                  fontFamily: 'Georgia, serif',
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                }}
              >
                Your Cards
              </span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                padding: 4,
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: SILVER_DIM,
              }}
            >
              <Icon name="close" size={14} color={SILVER_DIM} />
            </button>
          </div>

          {/* Card list */}
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {myCards.map((card) => (
              <PlayerCardView
                key={card.id}
                card={card}
                onReveal={() => revealCard(card.id)}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
}
